import React, { useState, useEffect, useCallback } from 'react';

const MathTrainingApp = () => {
  // State variables
  const [numberDigits, setNumberDigits] = useState('1'); // '1' or '2' for digit count
  const [answerMethod, setAnswerMethod] = useState('multiple'); // 'multiple' or 'manual'
  const [operation, setOperation] = useState('addition'); // 'addition', 'subtraction', 'multiplication', 'division'
  const [problem, setProblem] = useState({ num1: 0, num2: 0, answer: 0 });
  const [options, setOptions] = useState([]);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [showError, setShowError] = useState(false);

  // Encouraging messages for correct answers
  const encouragingMessages = [
    'Godt klaret!',
    'Flot arbejde!',
    'Det er rigtigt!',
    'Super indsats!',
    'Du har styr på det!'
  ];

  // Generate multiple choice options
  const generateOptions = useCallback((correctAnswer) => {
    const optionsList = [correctAnswer];
    
    // Generate 3 distractor answers within ±1-5 of the correct answer
    while (optionsList.length < 4) {
      const offset = Math.floor(Math.random() * 5) + 1;
      const sign = Math.random() < 0.5 ? -1 : 1;
      const newOption = correctAnswer + (offset * sign);
      
      // Only add if it's a whole number and not already in the list
      if (Number.isInteger(newOption) && newOption > 0 && !optionsList.includes(newOption)) {
        optionsList.push(newOption);
      }
    }
    
    // Shuffle the options
    setOptions(optionsList.sort(() => Math.random() - 0.5));
  }, []);

  // Generate a problem based on current settings
  const generateProblem = useCallback(() => {
    // Clear previous state
    setUserAnswer('');
    setFeedback('');
    
    // Determine max value based on digit selection
    const maxValue = numberDigits === '1' ? 9 : 99;
    const minValue = numberDigits === '1' ? 1 : 10;
    
    // Generate random numbers
    let num1 = Math.floor(Math.random() * (maxValue - minValue + 1)) + minValue;
    let num2 = Math.floor(Math.random() * (maxValue - minValue + 1)) + minValue;
    let answer = 0;
    
    // Ensure proper problem setup based on operation
    switch (operation) {
      case 'addition':
        answer = num1 + num2;
        break;
      case 'subtraction':
        // Ensure num1 >= num2 to avoid negative results
        if (num1 < num2) [num1, num2] = [num2, num1];
        answer = num1 - num2;
        break;
      case 'multiplication':
        answer = num1 * num2;
        break;
      case 'division':
        // Special handling for division to ensure whole number answers
        let divisor = num2;
        num1 = divisor * (Math.floor(Math.random() * 9) + 1); // Avoid multiplying by 0
        answer = num1 / divisor;
        break;
      default:
        break;
    }
    
    setProblem({ num1, num2, answer });
    
    // Generate multiple choice options if needed
    if (answerMethod === 'multiple') {
      generateOptions(answer);
    }
  }, [numberDigits, answerMethod, operation, generateOptions]);

  // useEffect to generate a problem on settings change
  useEffect(() => {
    generateProblem();
  }, [generateProblem]);

  // Handle answer submission
  const handleAnswerSubmit = () => {
    if (userAnswer === '') {
      setShowError(true);
      setTimeout(() => setShowError(false), 2000);
      return;
    }

    if (parseInt(userAnswer, 10) === problem.answer) {
      const randomMessage = encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)];
      setFeedback(randomMessage);
    } else {
      setFeedback(`Forkert. Det rigtige svar var ${problem.answer}.`);
    }
  };

  // Handle Enter key press for manual input
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleAnswerSubmit();
    }
  };

  // Render the component
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 space-y-6">
        
        {/* Header */}
        <h1 className="text-3xl font-bold text-center text-gray-800">Regnetræning</h1>
        
        {/* Problem Display */}
        <div className="text-center bg-gray-200 p-6 rounded-lg">
          <span className="text-5xl font-mono tracking-wider">{problem.num1} {
            {addition: '+', subtraction: '-', multiplication: '×', division: '÷'}[operation]
          } {problem.num2}</span>
        </div>

        {/* Answer Section */}
        {answerMethod === 'multiple' ? (
          <div className="grid grid-cols-2 gap-4">
            {options.map((opt, index) => (
              <button key={index} onClick={() => setUserAnswer(opt.toString())} className={`p-4 rounded-md text-lg font-semibold transition-colors ${userAnswer === opt.toString() ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}>
                {opt}
              </button>
            ))}
          </div>
        ) : (
          <input 
            type="number" 
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full p-4 text-center text-2xl border-2 border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Dit svar"
          />
        )}
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button onClick={handleAnswerSubmit} className="flex-1 px-6 py-3 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400" disabled={feedback !== ''}>
            Tjek svar
          </button>
          <button onClick={generateProblem} className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors">
            Ny opgave
          </button>
        </div>

        {/* Feedback Section */}
        {feedback && (
          <div className={`p-4 rounded-md text-center font-medium ${feedback.startsWith('Forkert') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
            {feedback}
          </div>
        )}
        
        {/* Error message for empty answer */}
        {showError && (
          <div className="p-3 rounded-md text-center font-medium bg-yellow-100 text-yellow-800">
            Du skal indtaste et svar.
          </div>
        )}

        {/* Help button */}
        <div className="text-center">
          <button 
            onClick={() => setShowHelp(true)}
            className="text-blue-600 hover:underline"
          >
            Hjælp
          </button>
        </div>
        
        {/* Settings */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600">Talstørrelse</label>
            <select value={numberDigits} onChange={(e) => setNumberDigits(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md">
              <option value="1">1-cifret</option>
              <option value="2">2-cifret</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">Svarmetode</label>
            <select value={answerMethod} onChange={(e) => setAnswerMethod(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md">
              <option value="multiple">Multiple Choice</option>
              <option value="manual">Manuelt</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">Regneart</label>
            <select value={operation} onChange={(e) => setOperation(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md">
              <option value="addition">+</option>
              <option value="subtraction">-</option>
              <option value="multiplication">*</option>
              <option value="division">/</option>
            </select>
          </div>
        </div>

        
        {/* Help popup */}
        {showHelp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-bold mb-3">Sådan bruger du appen</h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li><strong>Talstørrelse:</strong> Vælg mellem 1-cifrede (1-9) eller 2-cifrede (10-99) tal.</li>
                <li><strong>Svarmetode:</strong> Vælg mellem multiple choice eller manuel indtastning.</li>
                <li><strong>Regneart:</strong> Vælg den type regneopgave, du vil øve.</li>
                <li><strong>Tjek svaret:</strong> Klik for at se om dit svar er korrekt.</li>
                <li><strong>Ny opgave:</strong> Klik for at generere en ny opgave.</li>
              </ul>
              <button 
                onClick={() => setShowHelp(false)} 
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Luk
              </button>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default MathTrainingApp;